import React from 'react';
import { Box, Button } from '@mui/material';

/**
 * SectionNavigation Component
 *
 * Sticky navigation bar with section jumping
 * - Mobile: iOS-style equal-width tabs with bottom border indicator (no horizontal scroll)
 * - Desktop: Pill buttons with gaps
 *
 * @param {Object} props
 * @param {Array} props.sections - Array of section objects
 * @param {string} props.activeSection - Currently active section ID
 * @param {Function} props.onSectionClick - Callback when section button is clicked
 * @param {boolean} props.isMobile - Whether the viewport is mobile size
 */

const SectionNavigation = ({ sections, activeSection, onSectionClick, isMobile }) => {
  if (!sections || sections.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        width: '100%',
        marginBottom: isMobile ? '24px' : '32px'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          // Mobile: equal-width tabs filling the bar
          // Desktop: left-aligned pills with gaps
          ...(isMobile
            ? { width: '100%' }
            : { gap: '12px', padding: '12px 16px', justifyContent: 'flex-start' }
          ),
        }}
      >
        {sections.map((section) => {
          const isActive = activeSection === section.id;

          return isMobile ? (
            // MOBILE: iOS-style equal tab
            <Box
              key={section.id}
              onClick={() => onSectionClick(section.id)}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '10px 4px 8px',
                cursor: 'pointer',
                borderBottom: isActive
                  ? '2.5px solid #0978d6'
                  : '2.5px solid transparent',
                transition: 'all 0.2s ease',
                color: isActive ? '#0978d6' : '#8d9baa',
                '&:active': { backgroundColor: 'rgba(9,120,214,0.06)' },
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: sections.length > 3 ? '11px' : '12px',
                  fontWeight: isActive ? 700 : 500,
                  textAlign: 'center',
                  lineHeight: 1.2,
                  letterSpacing: '0.01em',
                }}
              >
                {section.title}
              </Box>
            </Box>
          ) : (
            // DESKTOP: pill buttons (existing style)
            <Button
              key={section.id}
              onClick={() => onSectionClick(section.id)}
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'none',
                padding: '8px 20px',
                borderRadius: '20px',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease',
                border: isActive ? 'none' : '1px solid #cbd5e1',
                backgroundColor: isActive ? '#0978d6' : 'transparent',
                color: isActive ? '#ffffff' : '#475569',
                '&:hover': {
                  backgroundColor: isActive ? '#0968c4' : 'rgba(9, 120, 214, 0.08)',
                  borderColor: isActive ? 'transparent' : '#0978d6',
                  color: isActive ? '#ffffff' : '#0978d6',
                },
              }}
            >
              {section.title}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};
export default SectionNavigation;
